import { useState } from "react"
import { InputField } from "@rawa/ui/InputField"
import { fieldInstructions, fieldName } from "../fields"
import type { ControlRenderer, RenderProps } from "./types"

// A text input with a decimal keypad rather than <input type="number">, as
// the GOV.UK and Australian Government design systems ask for numbers: the
// scroll wheel cannot change it, and what is typed is the control's own.
export const number: ControlRenderer<"number"> = {
  Render: (props) => <NumberField {...props} />,
}

/**
 * Holds what is typed, and reports the number it is: a decimal point being
 * typed stays on the page while the answer is the number so far.
 */
function NumberField({ field, value, onChange, error }: RenderProps<"number">) {
  const [text, setText] = useState(textOf(value))
  // An answer from elsewhere, such as a reloaded note, replaces what is typed
  // only when it is a different number, so 4.0 is not rewritten as 4.
  if (value !== numberIn(text)) setText(textOf(value))

  return (
    <InputField
      id={field.id}
      label={fieldName(field)}
      hint={fieldInstructions(field)}
      inputMode="decimal"
      value={text}
      onChange={(typed) => {
        if (!isNumberSoFar(typed)) return
        setText(typed)
        onChange(numberIn(typed))
      }}
      placeholder={field.placeholder}
      required={field.required}
      error={error}
    />
  )
}

// What can be typed on the way to a number: a minus to start, digits, and one
// decimal point. Two plain patterns rather than one with an optional point, so
// no run of digits can be split two ways and a failed match does not backtrack.
const WHOLE_SO_FAR = /^-?\d*$/
const DECIMAL_SO_FAR = /^-?\d*\.\d*$/
const isNumberSoFar = (text: string) =>
  WHOLE_SO_FAR.test(text) || DECIMAL_SO_FAR.test(text)

const textOf = (value: number | undefined) =>
  value === undefined ? "" : String(value)

/** The number typed so far, or none while there is none yet ("", "-", "."). */
function numberIn(text: string): number | undefined {
  const typed = Number(text)
  return text === "" || Number.isNaN(typed) ? undefined : typed
}
